import type { ComparisonEntry } from '../testing/GearTestOrchestrator';

export type FittingObjective = 'balanced' | 'distance' | 'accuracy';

/** Transparent, provisional utility—not a validated customer fitting model.
 * Spin affects flight; changing it is not intrinsically a penalty. Handicap is
 * retained for call compatibility, never treated as the customer's objective. */
export function fittingScore(entry: ComparisonEntry, baseline?: ComparisonEntry, _handicap = 15,
  objective: FittingObjective = 'balanced'): number {
  if (baseline && (!!baseline.pattern !== !!entry.pattern || (baseline.pattern && entry.pattern &&
    (baseline.pattern.cohortId !== entry.pattern.cohortId || baseline.pattern.shots.length !== entry.pattern.shots.length)))) return -Infinity;
  const carry = entry.pattern?.typicalCarry ?? Number(entry.formatted.carry);
  const miss = entry.pattern?.badMiss ?? Math.abs(Number(entry.formatted.offline));
  const width = entry.pattern?.landingWidth ?? 0;
  if (![carry, miss, width].every(Number.isFinite) || carry <= 0) return -Infinity;
  const [missWeight, widthWeight] = objective === 'accuracy' ? [2, .5]
    : objective === 'distance' ? [.35, .15] : [1, .35];
  return carry - miss * missWeight - width * widthWeight;
}

export function bestFittingEntry(entries: readonly ComparisonEntry[], baseline?: ComparisonEntry, handicap = 15,
  objective: FittingObjective = 'balanced') {
  const core = entries.find(entry => entry.recommendationSource === 'core');
  if (core) return core;
  const valid = entries.filter(entry => Number.isFinite(fittingScore(entry, baseline, handicap, objective)));
  const best = valid.reduce<ComparisonEntry | null>((best, entry) =>
    !best || fittingScore(entry, baseline, handicap, objective) > fittingScore(best, baseline, handicap, objective) ? entry : best, null);
  // Within one utility point, prefer keeping the current setup when available.
  if (best && baseline && valid.includes(baseline) && similarPerformance(best, baseline, objective)) return baseline;
  return best;
}

/** A practical indifference band, NOT a statistical/model confidence interval. */
export function similarPerformance(a: ComparisonEntry, b: ComparisonEntry, objective: FittingObjective = 'balanced'): boolean {
  const aScore = fittingScore(a, undefined, 15, objective), bScore = fittingScore(b, undefined, 15, objective);
  return Number.isFinite(aScore) && Number.isFinite(bScore) && Math.abs(aScore - bScore) <= 1;
}
